import {
  UseQueryOptions,
  useQueries,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  ActivityValue,
  FlatActivityModel,
  ValidationStatus
} from 'types/shared'
import { AllowedLocales } from 'utils/date'

import {
  useValidateActivityValue,
  UseValidateActivityValueProps,
  ValidateFlatActivityValueProps
} from 'utils/validateActivityValue'

type ValidableActivity = Pick<
  ValidateFlatActivityValueProps,
  'activityModel' | 'value'
>

type ValidationResult = {
  validationStatus: ValidationStatus
  helperText?: {
    des: string
    complement?: string
  }
}

const useValidationErrorContext =
  (): UseValidateActivityValueProps['context'] => {
    const { language } = useTranslation()

    return useMemo(
      () => ({
        lang: language as AllowedLocales
      }),
      [language]
    )
  }

export const useImperativeValidateActivities = () => {
  const queryClient = useQueryClient()
  const context = useValidationErrorContext()
  const { validateFlatActivityValue } = useValidateActivityValue({ context })

  const validateActivities = useCallback(
    async (activities: ValidableActivity[]) =>
      Promise.all(
        activities.map(({ activityModel, value }) =>
          queryClient.fetchQuery({
            queryKey: ['validateFlatActivityValue', activityModel.uuid, value],
            queryFn: () => validateFlatActivityValue({ activityModel, value }),
            initialData: {
              validationStatus: 'pending' as ValidationStatus
            } as ValidationResult
          })
        )
      ),
    [context, queryClient, validateFlatActivityValue]
  )

  return validateActivities
}

type UseValidateActivitiesProps = {
  activities: ValidableActivity[]
  enabled?: boolean
}

export const useValidateActivities = ({
  activities,
  enabled
}: UseValidateActivitiesProps) => {
  const context = useValidationErrorContext()
  const { validateFlatActivityValue } = useValidateActivityValue({ context })

  const results =
    useQueries({
      queries: activities.map(
        ({ activityModel, value }) =>
          ({
            queryKey: ['validateFlatActivityValue', activityModel.uuid, value],
            queryFn: () => validateFlatActivityValue({ activityModel, value }),
            initialData: {
              validationStatus: 'pending' as ValidationStatus
            } as ValidationResult,
            enabled
          }) satisfies UseQueryOptions
      )
    }) || []

  type Result = (typeof results)[number]

  type ResultWithInitialData = Omit<Result, 'data'> & {
    data: NonNullable<Result['data']>
  }

  return results as ResultWithInitialData[]
}

type UseValidateActivityProps = {
  activityModel: FlatActivityModel
  value?: ActivityValue
  enabled?: boolean
  debounceTime?: number
}

export default function useValidateActivity({
  activityModel,
  value,
  enabled,
  debounceTime = 300
}: UseValidateActivityProps) {
  const [activities, setActivities] = useState([{ activityModel, value }])

  const debounceActivityValue = () => {
    const timeout = setTimeout(
      () => setActivities([{ activityModel, value }]),
      debounceTime
    )

    return () => clearTimeout(timeout)
  }

  useEffect(debounceActivityValue, [activityModel, value, debounceTime])

  const [{ data, ...queryInfo }] = useValidateActivities({
    activities,
    enabled
  })

  return {
    ...data,
    ...queryInfo
  }
}
